import React, { useRef, useState, useEffect } from 'react'
import './style.scss'
import { useMutation, useQuery } from '@apollo/client'
import { GetBlueprint, GetOrderByNumber, GetProdCode, GetProduction, SearchClients } from '../../graphql/queries'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import DateInput from '../../components/DateInput'
import Input from '../../components/Input'
import Checkbox from '../../components/Checkbox'
import Textarea from '../../components/Textarea'
import BoxWorkflow from '../../components/BoxWorkflow'
import { DuplicateProductionItem, SendOrderEmail, SetProductionToOrder, UpdateProductionItem } from '../../graphql/mutations'
import ActivityIndicator from '../../components/ActivityIndicator'
import { getUTCDate, parseDate } from '../../util/format'
import BackButton from '../../components/BackButton'
import Button from '../../components/Button'
import SelectInput from '../../components/SelectInput'
import CreatableInputEdit from '../../components/CreatableInputEdit'
import Printouts from '../../components/Printouts'
import EmailLog from '../../components/EmailLog'
import Modal from '../../components/Modal'
import PdfViewer from '../../components/PdfViewer'
import { useNotification } from '../../providers/Notification'
import OrderAltDocument from '../../documents/order-document-alt'

import { pdf } from '@react-pdf/renderer'
import ClientBoxcode from '../../components/ClientBoxcode'
import { Warehouses } from '../../util/const'

const SalesScreen = () => {

    const { t } = useTranslation()
    const navigate = useNavigate()
    const { id } = useParams()
    const { dispatch } = useNotification()

    const initialTransportOptions = [
        {
            value: 'Multipakend',
            label: t('Multipakend'),
        },
        {
            value: 'Klient',
            label: t('Klient'),
        },
        {
            value: 'Tellida',
            label: t('Tellida'),
        },
    ]
    
    const [prodData, setProdData] = useState(null)
    const [modifiedFields, setModifiedFields] = useState([])
    const [transportOptions, setTransportOptions] = useState(initialTransportOptions)
    const [clientTransports, setClientTransports] = useState([])
    const [clientContacts, setClientContacts] = useState([])
    const [orderNumber, setOrderNumber] = useState(null)
    const [showModal, setShowModal] = useState(false)
    const [orderData, setOrderData] = useState(null)

    const [makeOrder] = useMutation(SetProductionToOrder)
    const [duplicateRow] = useMutation(DuplicateProductionItem)
    const [sendEmail] = useMutation(SendOrderEmail)

    const { loading, refetch } = useQuery(GetProduction, {
        variables: {
          id: parseInt(id)
        },
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            const { getProduction } = data

            if (getProduction) {
                setProdData(getProduction)
                if (getProduction.client) {
                    if (getProduction.client.contacts) {
                        setClientContacts(getProduction.client.contacts)
                    }
                    if (getProduction.client.transports) {
                        setClientTransports(getProduction.client.transports)
                    }
                    if (getProduction.client.TRANSFIRMA) {
                        setTransportOptions([
                            {
                                label: getProduction.client.TRANSFIRMA,
                                value: getProduction.client.TRANSFIRMA,
                            },
                            ...transportOptions,
                        ])
                    }
                }
                setOrderNumber(getProduction.order && getProduction.order.ORDERNO ? getProduction.order.ORDERNO : null)
            }
        },
    })

    const { data: prodCodeData } = useQuery(GetProdCode, {
        variables: {
            id: parseInt(prodData?.PROD_CODE),
        },
        skip: !prodData?.PROD_CODE,
    })

    const { refetch: searchClients } = useQuery(SearchClients, {
        variables: {
            searchInput: '',
        },
    })

    const { data: blueprintData } = useQuery(GetBlueprint, {
        skip: !prodData?.PRODUCTION_CODE,
        variables: {
            productionCode: prodData?.PRODUCTION_CODE,
        },
    })

    const { refetch: getOrderByNumber } = useQuery(GetOrderByNumber, {
        skip: true,
    })

    const [updateProductionItem, { loading: updateLoading }] = useMutation(UpdateProductionItem)

    const setField = (field, value) => {
        const finalValue = field === 'PRICE' ? parseFloat(value) : value
        setProdData({
            ...prodData,
            [field]: finalValue,
        })

        if (!modifiedFields.includes(field)) {
            setModifiedFields([...modifiedFields,field])
        }
    }

    const duplicate = async () => {
        try {
            const { data } = await duplicateRow({
                variables: {
                    id: parseInt(prodData.ID),
                },
            })
            if (data && data.duplicateProductionItem !== 'Fail') {
                dispatch({
                    type: 'ADD',
                    payload: {
                        content: t('Tellimus kopeeritud'),
                        type: 'success',
                    },
                })
                navigate(`/sale/${data.duplicateProductionItem}`)
            }
        } catch (err) {
            console.log('duplicate', err)
        }
    }

    const loadClients = async (input) => {
        if (input.length < 3) return
        const res = await searchClients({
            searchInput: input,
        })
        return res.data.searchClients
    }

    const updateProperty = async () => {
        if (modifiedFields.length === 0) {
            return
        }
        const changedData = {}
            modifiedFields.forEach(field => {
            changedData[field] = prodData[field]
        })
        try {
            const result = await updateProductionItem({
                variables: {
                    id: parseInt(id),
                    data: changedData,
                },
            })
            if (changedData.FIN_DATE) {
                if (!['Fail', 'Success'].includes(result?.data?.updateProductionItem) && parseInt(result?.data?.updateProductionItem) > 0) {
                    dispatch({
                        type: 'ADD',
                        payload: {
                            content: `Selle tellimuse ${result?.data?.updateProductionItem} rida on veel täitmata`,
                            type: 'info',
                        },
                    })
                    return
                }
                refetch()
            }
            
            if (result?.data?.updateProductionItem === 'Success') {
                setModifiedFields([])
            }
        } catch (err) {
            console.log('updateProperty:', err)
        }
    }

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (modifiedFields.length > 0) {
                event.preventDefault()
                event.returnValue = '' 
            }
        }
    
        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [modifiedFields])

    const handleClientChange = (client) => {
        if (!client || !client.ID) return
        setProdData({
            ...prodData,
            client,
            KLIENT_ID: client?.ID
        })
       if (!modifiedFields.includes('KLIENT_ID')) {
        setModifiedFields([...modifiedFields,'KLIENT_ID'])
       }
    }

    const getMaterialDeliveryAmount = () => {
        if (prodData && prodData.materialDeliveries) {
            const total = prodData.materialDeliveries.reduce((acc, curr) => {
                if (curr.AMOUNT) acc += curr.AMOUNT
                return acc
            }, 0)
            return total ? total : ''
        }
        return ''
    }

    const getExpectedPalletsAmount = () => {
        const deliveredMaterialAmount = getMaterialDeliveryAmount()
        const pcsPerHit = blueprintData?.getBlueprint?.PCS_PER_HIT
        const amountPallet = blueprintData?.getBlueprint?.AMOUNT_PALLET

        if (!deliveredMaterialAmount || !pcsPerHit || !amountPallet) return '-'

        return (deliveredMaterialAmount * pcsPerHit / amountPallet)?.toFixed(2)
    }

    const viewQuotation = async () => {
        try {
            const { data } = await getOrderByNumber({
                orderNumber: parseInt(orderNumber),
            })

            if (data && data.getOrderByNumber) {
                navigate(`/order/${data.getOrderByNumber.ID}`, {
                    state: {
                        rowId: prodData.ORDER_ROW_ID,
                    },
                })
            }
        } catch (err) {
            console.log('Error getting quotation', err)
        }
    }

    const handleQuotationNumberChange = async (e) => {
        try {
            setOrderNumber(e.target.value)

            if (!e.target.value) return

            const { data } = await getOrderByNumber({
                orderNumber: parseInt(e.target.value)
            })
            
            if (data && data.getOrderByNumber) {
                setField('ORDER_ID', data.getOrderByNumber.ID)
            }
        } catch (err) {
            console.log('handleQuotationNumberChange', err)
        }
    }

    const handleMakeOrderPreview = () => {
        const updatedData = [
            {
                ...prodData,
                contact: clientContacts.find(x => x.ID === prodData.CONTACT_ID),
            },
        ]
        setOrderData(updatedData)

        handleShowModal()
    }

    const handleMakeOrder = async () => {
        if (!prodData.ORDER_DATE || !prodData.KLIENT_ORDER || prodData.KLIENT_ORDER === '0') {
            dispatch({
                type: 'ADD',
                payload: {
                    content: t('Kontrolli kliendi tellimuse numbrit ja kuupäeva'),
                    type: 'error',
                },
            })
            return
        }
        try {
            const { data } = await makeOrder({
                variables: {
                    ids: [parseInt(id)],
                },
            })

            if (data?.setProductionToOrder) {
                handleCloseModal()
                const updatedData = [
                    {
                        ...prodData,
                        TELLIMUSNO: data.setProductionToOrder,
                        contact: clientContacts.find(x => x.ID === prodData.CONTACT_ID),
                    },
                ]
                setOrderData(updatedData)
                handleShowModal()

                handleEmail(updatedData, parseInt(data.setProductionToOrder))
            }
        } catch (err) {
            console.log('openPdf:', err)
        }
    }

    const handleEmail = async (documentData, orderNo) => {
        if (!documentData || !orderNo) return

        try {
            const pdfFile = pdf(<OrderAltDocument data={documentData} />)
            pdfFile.updateContainer(<OrderAltDocument data={documentData} />)
            const blob = await pdfFile.toBlob()
            const file = new File([blob], 'file.pdf', { type: 'application/pdf'})

            const res = await sendEmail({
                variables: {
                    orderNo,
                    file,
                },
            })

            if (res?.data?.sendOrderEmail === 'Success') {
                dispatch({
                    type: 'ADD',
                    payload: {
                        type: 'success',
                        content: t('Email saadetud')
                    },
                })
            } else {
                dispatch({
                    type: 'ADD',
                    payload: {
                        type: 'error',
                        content: t('Emaili saatmine ebaõnnestus')
                    },
                })
            }
        } catch (err) {
            console.log('handleEmail:', err)
            dispatch({
                type: 'ADD',
                payload: {
                    type: 'error',
                    content: t('Emaili saatmine ebaõnnestus')
                },
            })
        }
    }

    const handleCopyContact = () => {
        const contact = clientContacts.find(x => x.ID === prodData.CONTACT_ID)
        navigator.clipboard.writeText(contact.EMAIL)
        
        dispatch({
            type: 'ADD',
            payload: {
                type: 'success',
                content: t('Kontakt kopeeritud')
            },
        })
    }

    const handleCopyCompany = () => {
        navigator.clipboard.writeText(prodData.client.NAME)
        
        dispatch({
            type: 'ADD',
            payload: {
                type: 'success',
                content: t('Klient kopeeritud')
            },
        })
    }

    const handleShowModal = () => {
        setShowModal(true)
    }

    const handleCloseModal = () => {
        setShowModal(false)
    }

    const handlePrevNext = async (direction) => {
        try {
            const rows = JSON.parse(localStorage.getItem('sales'))
            const currentIndex = rows.findIndex(x => x.ID === prodData.ID)

            if (direction === 'prev' && currentIndex <= 0) return
            if (direction === 'next' && currentIndex >= rows.length - 1) return

            const id = direction === 'next' ? rows[currentIndex + 1]?.ID : rows[currentIndex - 1]?.ID
            navigate(`/sale/${id}`)
        } catch (err) {
            console.log('HandlePrevNext', err)
        }
    }

    if (loading) return (
        <div className='loading-view'>
            <ActivityIndicator />
        </div>
    )

    return (
        <div className='sales'>
            <div className='inner'>
                <div className='inner-title'>
                    <h2>{ t('Müük') }</h2>
                    <div className='inner-title--actions'>
                        <Button
                            label={t('<')}
                            onClick={() => handlePrevNext('prev')}
                        />
                        <Button
                            label={t('>')}
                            onClick={() => handlePrevNext('next')}
                        />
                        <Button
                            label={t('Tee tellimusest koopia')}
                            onClick={async () => {
                                updateProperty()
                                duplicate()
                            }}
                        />
                        <Button
                            label={t('Koosta tellimus')}
                            onClick={async () => {
                                updateProperty()
                                handleMakeOrderPreview()
                                }
                            }
                        />
                        <Button
                            label={t('Salvesta')}
                            onClick={() => updateProperty()}
                            disabled={modifiedFields.length === 0 || updateLoading}
                            loading={updateLoading}
                        />
                    </div>
                </div>
                { prodData ?
                    <div className='sales-fields'>
                        <div className='editable-fields'>
                            <div className='left-col'>
                                <div className='field-row'>
                                    <div className='field-col'>
                                        <Input
                                            label={t('Number')}
                                            type={'number'}
                                            value={prodData.ORDERNO}
                                            onChange={(e) => setField('ORDERNO', parseInt(e.target.value))}
                                        />
                                        <DateInput
                                            label={t('Tellimuse kuupäev')}
                                            value={prodData.ORDER_DATE && prodData.ORDER_DATE !== '0000-00-00' ? new Date(prodData.ORDER_DATE) : null}
                                            onChange={(val) => setField('ORDER_DATE', parseDate(val))}
                                        />
                                        <DateInput
                                            label={t('Tähtaeg')}
                                            value={prodData.DEADLINE && prodData.DEADLINE !== '0000-00-00' ? new Date(prodData.DEADLINE) : null}
                                            onChange={(val) => setField('DEADLINE', parseDate(val))}
                                        />
                                        <DateInput
                                            label={t('Reaalne tähtaeg')}
                                            value={prodData.NEW_DEADLINE && prodData.NEW_DEADLINE !== '0000-00-00' ? new Date(prodData.NEW_DEADLINE) : null}
                                            onChange={(val) => setField('NEW_DEADLINE', parseDate(val))}
                                        />
                                        <DateInput
                                            label={t('Tootmise algus')}
                                            value={prodData.START_DATE && prodData.START_DATE !== '0000-00-00' ? new Date(prodData.START_DATE) : null}
                                            onChange={(val) => setField('START_DATE', parseDate(val))}
                                        />
                                    </div>
                                    <div className='field-col'>
                                        <DateInput
                                            label={t('Tootmise lõpp')}
                                            value={prodData.FIN_DATE && prodData.FIN_DATE !== '0000-00-00' ? new Date(prodData.FIN_DATE) : null}
                                            onChange={(val) => setField('FIN_DATE', parseDate(val))}
                                        />
                                        <DateInput
                                            label={t('Väljastatud')}
                                            value={prodData.SHIPPED  && prodData.SHIPPED !== '0000-00-00' ? new Date(prodData.SHIPPED) : null}
                                            onChange={(val) => setField('SHIPPED', parseDate(val))}
                                        />
                                        <Input
                                            label={t('Aluseid')}
                                            type={'text'}
                                            value={prodData.ALUSEID}
                                            onChange={(e) => setField('ALUSEID', e.target.value)}
                                        />
                                        <Input
                                            label={t('Tegelik kogus')}
                                            type={'number'}
                                            value={prodData.RKOGUS}
                                            onChange={(e) => setField('RKOGUS', parseInt(e.target.value))}
                                        />
                                    </div>
                                </div>
                                <div className='field-col box-fields--wrapper'>
                                    <div className='box-fields'>
                                        <Input
                                            label={t('Kogus')}
                                            value={prodData.KOGUS}
                                            type={'number'}
                                            onChange={(e) => setField('KOGUS', parseInt(e.target.value))}
                                        />
                                        <Input
                                            label={t('Hind')}
                                            value={prodData.PRICE}
                                            type={'number'}
                                            onChange={(e) => setField('PRICE', e.target.value)}
                                        />
                                        <div className='box-fields-item'>
                                            <span className='box-fields-item--label'>{ t('Tüüp') }</span>
                                            <span className='box-fields-item--value'>{ prodData.BOX }</span>
                                        </div>
                                        <div className='box-fields-item'>
                                            <span className='box-fields-item--label'>{ t('Trükk') }</span>
                                            <span className='box-fields-item--value'>{ prodData.TRYKK }</span>
                                        </div>
                                        <div className='box-fields-item'>
                                            <span className='box-fields-item--label'>{ t('Materjal') }</span>
                                            <span className='box-fields-item--value'>{ prodData.MATERIAL }</span>
                                        </div>
                                        <div className='box-fields-item'>
                                            <span className='box-fields-item--label'>{ t('FSC') }</span>
                                            <span className='box-fields-item--value'>{ prodData.FSC }</span>
                                        </div>
                                        <div className='box-fields-item'>
                                            <span className='box-fields-item--label'>{ t('Mõõt') }</span>
                                            <span className='box-fields-item--value'>{ prodData.MOOT }</span>
                                        </div>
                                        <div className='box-fields-item'>
                                            <span className='box-fields-item--label'>{ t('Kliendi tootekood') }</span>
                                            <span className='box-fields-item--value'>{ prodData.KLIENT_BOXCODE }</span>
                                        </div>
                                        <div className='box-fields-item'>
                                            <span className='box-fields-item--label'>{ t('Toote kood') }</span>
                                            <span className='box-fields-item--value'>{ prodData.PRODUCTION_CODE }</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='right-col'>
                                <div className='field-row'>
                                    
                                    <div className='field-col'>
                                        <div className='related-order'>
                                            <Input
                                                label={t('Hinnapakkumine')}
                                                type={'number'}
                                                value={orderNumber}
                                                onChange={handleQuotationNumberChange}
                                            />
                                            <Button
                                                label={t('Hinnapakkumine')}
                                                onClick={viewQuotation}
                                            />
                                        </div>
                                        <Textarea
                                            label={t('Kommentaar')}
                                            value={prodData.KOMMENTAAR}
                                            onChange={(e) => setField('KOMMENTAAR', e.target.value)}
                                        />
                                        <Input
                                            label={t('Mõõt')}
                                            type={'text'}
                                            value={prodData.ALUS_MOOT}
                                            onChange={(e) => setField('ALUS_MOOT', e.target.value)}
                                        />
                                    </div>
                                    <div className='field-col'>
                                        <h5>{ t('Maksumused') }</h5>
                                        <Input
                                            label={t('Stants')}
                                            type={'number'}
                                            value={prodData.STANTS}
                                            onChange={(e) => setField('STANTS', parseFloat(e.target.value))}
                                        />
                                        <Input
                                            label={t('Klishee')}
                                            type={'number'}
                                            value={prodData.KLISHEE}
                                            onChange={(e) => setField('KLISHEE', parseFloat(e.target.value))}
                                        />
                                        <Input
                                            label={t('Transport')}
                                            type={'number'}
                                            value={prodData.TRANSPORT}
                                            onChange={(e) => setField('TRANSPORT', parseFloat(e.target.value))}
                                        />
                                        <Input
                                            label={t('Alus')}
                                            type={'number'}
                                            value={prodData.PALLET}
                                            onChange={(e) => setField('PALLET', parseFloat(e.target.value))}
                                        />
                                    </div>
                                </div>

                                <div className='field-col'>
                                    <Printouts
                                        production={prodData}
                                        onUpdateField={updateProperty}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='client-fields'>
                            <div className='field-row bottom-fields'>
                                <div className='field-col field-col--client'>
                                    <h5>{ t('Klient') }</h5>
                                    <div className='field-row'>
                                        <div className='field-col'>
                                            <div className='company-field'>
                                                <SelectInput
                                                    label={`${t('Nimi')}`}
                                                    value={prodData.client}
                                                    load={loadClients}
                                                    getOptionLabel={(option) => option.NAME}
                                                    getOptionValue={(option) => option.ID}
                                                    loadingMessage={() => t('Otsin kliente')}
                                                    noOptionsMessage={() => t('Otsi..')}
                                                    onChange={handleClientChange}
                                                    defaultValue={prodData.client}
                                                />
                                                <Button
                                                    className={'btn-small'}
                                                    label={t('Kopeeri klient')}
                                                    onClick={handleCopyCompany}
                                                />
                                            </div>
                                            <Input
                                                label={t('Tellimuse number')}
                                                value={prodData.KLIENT_ORDER}
                                                onChange={(e) => setField('KLIENT_ORDER', e.target.value)}
                                            />
                                            <ClientBoxcode
                                                production={prodData}
                                            />
                                            <div className='field-row'>
                                                <Checkbox
                                                    label={t('Võlgnik')}
                                                    value={prodData.client.DEPT === 1}
                                                    disabled={true}
                                                />
                                                <Checkbox
                                                    label={t('Multipakendi firma')}
                                                    value={prodData.client.COMMON === 1}
                                                    disabled={true}
                                                />
                                            </div>
                                            <div className='field-row'>
                                                <Checkbox
                                                    label={t('Ettemaks on tehtud')}
                                                    value={prodData.ETTEMAKS === 1}
                                                    onChange={(e) => setField('ETTEMAKS', e.target.checked ? 1 : 0)}
                                                />
                                                <Checkbox
                                                    label={t('Ettemaks on vajalik')}
                                                    value={prodData.client && prodData.client.ETTEMAKS === 1}
                                                    disabled={true}
                                                />
                                            </div>
                                        </div>
                                        <div className='field-col'>
                                            <div className='field-row field-row--contact'>
                                                <SelectInput
                                                    label={`${t('Kontakt')}`}
                                                    value={clientContacts.find(x => x.ID === prodData.CONTACT_ID)}
                                                    options={clientContacts}
                                                    getOptionLabel={(option) => option.EMAIL}
                                                    getOptionValue={(option) => option.ID}
                                                    onChange={(val) => setField('CONTACT_ID', val.ID)}
                                                />
                                                <Button
                                                    className={'btn-small'}
                                                    label={t('Kopeeri kontakt')}
                                                    onClick={handleCopyContact}
                                                />
                                            </div>
                                            <CreatableInputEdit
                                                label={`${t('Transport')}`}
                                                value={prodData.TRANSPORDIFIRMA ? {
                                                    label: prodData.TRANSPORDIFIRMA,
                                                    value: prodData.TRANSPORDIFIRMA,
                                                } : null}
                                                options={transportOptions}
                                                noOptionsMessage={() => t('Lisa uus transport')}
                                                onChange={(option) => {setField('TRANSPORDIFIRMA', option ? option.value : null)}}
                                                placeholder={'Vali või sisesta'}
                                            />
                                            <Textarea
                                                label={t('Tarne kommentaar')}
                                                value={prodData.DELIVERY_COMMENT}
                                                onChange={(e) => setField('DELIVERY_COMMENT', e.target.value)}
                                            />
                                            <div className='field-row' style={{ marginTop: '10px', alignItems: 'flex-end' }}>
                                                <DateInput
                                                    label={t('Transport tellitud')}
                                                    value={prodData.TRANSPORT_DATE && prodData.TRANSPORT_DATE !== '0000-00-00' ? new Date(prodData.TRANSPORT_DATE) : null}
                                                    onChange={(val) => setField('TRANSPORT_DATE', getUTCDate(val))}
                                                />
                                                <Checkbox
                                                    label={t('Klient teavitatud')}
                                                    value={prodData.KLIENT_INFORMED === 1}
                                                    onChange={(e) => setField('KLIENT_INFORMED', e.target.checked ? 1 : 0)}
                                                />
                                            </div>
                                            <div className='field-row'>
                                                <Input
                                                    label={t('Eeldatav aluste arv')}
                                                    value={getExpectedPalletsAmount()}
                                                    disabled
                                                />
                                                <Input
                                                    label={t('Toote kaal')}
                                                    value={prodCodeData?.getProdCode?.weight}
                                                    suffix={'kg/tk'}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                        <div className='field-col'>
                                            {
                                                clientTransports && clientTransports.length > 0 && clientTransports[0].TRANSPORT ?
                                                <SelectInput
                                                    label={t('Aadressid')}
                                                    value={clientTransports.find(x => x.TRANSPORT === prodData.ADDRESS)}
                                                    options={clientTransports}
                                                    getOptionLabel={(option) => option.TRANSPORT}
                                                    getOptionValue={(option) => option.TRANSPORT}
                                                    onChange={(option) => setField('ADDRESS', option.TRANSPORT)}
                                                />
                                                :
                                                <></>
                                            }
                                            <Textarea
                                                label={t('Aadress')}
                                                value={prodData.ADDRESS}
                                                onChange={(e) => setField('ADDRESS', e.target.value)}
                                            />
                                            <SelectInput
                                                label={t('Ladu')}
                                                value={Warehouses.find(x => x.value === prodData.WAREHOUSE)}
                                                options={Warehouses}
                                                onChange={(option) => setField('WAREHOUSE', option.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <EmailLog
                            emails={prodData.emails}
                        />
                    </div>
                    :
                    <div className='empty-result'>
                        <p>{ t('Production not found') }</p>
                    </div>
                }
            </div>
            <Modal
                show={showModal}
                className={'make-order-modal'}
                close={handleCloseModal}
            >
                {
                    orderData &&
                    <PdfViewer document={'order'} data={orderData} onSendEmail={handleMakeOrder} />
                }
            </Modal>
        </div>
    )
}

export default SalesScreen